import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { surveillanceApi } from '../../api/surveillanceApi';
import { EstablishmentStatusChange, SurveillanceParams } from '../../types/surveillance';
import { RootState } from '../index';

// Define the surveillance tab type
export type SurveillanceTabType = 'bodacc' | 'siret';
export const SurveillanceTabValues = {
  BODACC_ANOMALIES: 'bodacc' as SurveillanceTabType,
  SIRET_SURVEILLANCE: 'siret' as SurveillanceTabType,
};

interface SurveillanceState {
  currentTab: SurveillanceTabType;
  // SIRET surveillance data
  establishments: EstablishmentStatusChange[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  // Filters
  filters: {
    fromDate?: string;
    toDate?: string;
    status: 'opened' | 'closed' | 'all';
  };
}

const initialState: SurveillanceState = {
  currentTab: SurveillanceTabValues.BODACC_ANOMALIES,
  establishments: [],
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 20,
  filters: {
    status: 'all'
  }
};

// Async thunk for fetching establishment changes
export const fetchEstablishmentChanges = createAsyncThunk(
  'surveillance/fetchEstablishmentChanges',
  async (params: SurveillanceParams, { rejectWithValue }) => {
    try {
      const response = await surveillanceApi.getEstablishmentChanges(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erreur lors de la récupération des changements d\'établissements');
    }
  }
);

const surveillanceSlice = createSlice({
  name: 'surveillance',
  initialState,
  reducers: {
    setCurrentTab: (state, action: PayloadAction<SurveillanceTabType>) => {
      state.currentTab = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<SurveillanceState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.establishments = [];
      state.currentPage = 1;
      state.totalPages = 0;
      state.totalItems = 0;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEstablishmentChanges.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEstablishmentChanges.fulfilled, (state, action) => {
        state.isLoading = false;
        state.establishments = action.payload.data;
        state.currentPage = action.payload.pagination.currentPage;
        state.totalPages = action.payload.pagination.totalPages;
        state.totalItems = action.payload.pagination.totalItems;
        state.itemsPerPage = action.payload.pagination.itemsPerPage;
        state.error = null;
      })
      .addCase(fetchEstablishmentChanges.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.establishments = [];
      });
  }
});

export const {
  setCurrentTab,
  setCurrentPage,
  setFilters,
  clearError,
  resetState
} = surveillanceSlice.actions;

// Selectors
export const selectCurrentTab = (state: RootState) => state.surveillance.currentTab;
export const selectEstablishments = (state: RootState) => state.surveillance.establishments;
export const selectIsLoading = (state: RootState) => state.surveillance.isLoading;
export const selectError = (state: RootState) => state.surveillance.error;
export const selectCurrentPage = (state: RootState) => state.surveillance.currentPage;
export const selectTotalPages = (state: RootState) => state.surveillance.totalPages;
export const selectTotalItems = (state: RootState) => state.surveillance.totalItems;
export const selectItemsPerPage = (state: RootState) => state.surveillance.itemsPerPage;
export const selectFilters = (state: RootState) => state.surveillance.filters;

export default surveillanceSlice.reducer; 